import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from "@angular/router";

import { DateTime } from 'luxon';
import { firstValueFrom } from 'rxjs';

import { User } from "../interfaces/user";

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  http = inject(HttpClient);
  router = inject(Router);

  user = signal<User>(undefined);
  isLoggedIn = false;
  authorization = signal({
    player: false,
    marshal: false,
    master: false
  });

  isPlayer = computed(() => {
    return this.authorization().player;
  });
  isMarshal = computed(() => {
    return this.authorization().marshal;
  });
  isMaster = computed(() => {
    return this.authorization().master;
  });
  constructor () {
    this.determineAuth(this.getUser());
  };

  setUser (user: User | undefined): void {
    this.user.set(user);
  };

  getUser (): User | undefined {
    if (this.user() == null) {
      let storedValue = localStorage.getItem('loggedInUser');
      if (storedValue) {
        let userStorage = JSON.parse(storedValue);
        let now = DateTime.now();
        let expiration = DateTime.fromISO(userStorage.expiration);
        if (now < expiration) {     //  && userStorage.user.role
          this.setUser(userStorage.user);
        }
        else {
          localStorage.removeItem('loggedInUser');
        }
      }
    }
    return this.user();
  };

  determineAuth (user: User | undefined) {
    this.isLoggedIn = false;
    if (user != null) {
      this.isLoggedIn = true;
      this.authorization.set({
        player: user.role != null,
        marshal: user.role === "Marshal" || user.role === "Master",
        master: user.role === "Master"
      });
    }
  };

  clearAuth () {
    this.isLoggedIn = false;
    this.authorization.set({
      player: false,
      marshal: false,
      master: false
    });
  };

  newSession (email: String, password: String) {
    return this.http.post<User>('/api/session', {
      email: email,
      password: password
    });
  };

  async establishServerSession (
    email: String,
    password: String,
    remember: boolean = false) {
    let user = await firstValueFrom(this.newSession(email, password));
    let days = remember ? 7 : 1;
    let userStorage = {
      user: user,
      expiration: DateTime.now().plus({ days: days }).toISO()
    };
    localStorage.setItem('loggedInUser', JSON.stringify(userStorage));
    return user;
  };

  processUser (user: User) {
    this.setUser(user);
    this.determineAuth(user);
    return this.router.navigate(['/user/assignments']);
  };

  async login (accountName: string, password: string, remember: boolean) {
    try {
      let user = await this.establishServerSession(accountName, password, remember);
      return this.processUser(user);
    }
    catch (error) {
      throw error;
    }
  };
}
